import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiEdit2, FiList, FiSave, FiTrash2, FiX } from 'react-icons/fi';
import { server } from '../../server';

const ManageSubscriptionCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCoupon, setEditingCoupon] = useState(null);

  useEffect(() => {
    const fetchCoupons = async () => {
      try {
        const { data } = await axios.get(`${server}/subscriptioncouponcode/all-subscription-coupons`, {
          withCredentials: true,
        });
        if (data.success) {
          setCoupons(Array.isArray(data.coupons) ? data.coupons : []);
        } else {
          toast.error('Failed to load coupon codes');
        }
      } catch (error) {
        toast.error(error.response?.data?.message || 'Error fetching coupons');
      } finally {
        setLoading(false);
      }
    };

    fetchCoupons();
  }, []);

  const handleDeleteCoupon = async (id) => {
    try {
      const { data } = await axios.delete(`${server}/subscriptioncouponcode/delete-subscription-coupon/${id}`, {
        withCredentials: true,
      });
      toast.success(data.message);
      setCoupons(coupons.filter((coupon) => coupon._id !== id));
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting coupon');
    }
  };

  const handleEditCoupon = async (coupon) => {
    setEditingCoupon(coupon);
  };

  const handleSaveEdit = async () => {
    try {
      const { data } = await axios.put(
        `${server}/subscriptioncouponcode/edit-subscription-coupon/${editingCoupon._id}`,
        editingCoupon,
        {
          withCredentials: true,
        }
      );
      toast.success(data.message);
      setCoupons((prevCoupons) =>
        prevCoupons.map((coupon) => (coupon._id === editingCoupon._id ? editingCoupon : coupon))
      );
      setEditingCoupon(null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating coupon');
    }
  };

  const formatDiscount = (coupon) => {
    if (coupon.discountType === 'percentage') {
      return `${coupon.discountAmount}%`;
    }

    return `$${coupon.discountAmount}`;
  };

  const getStatusClassName = (status) => {
    const normalizedStatus = String(status || 'active')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    return `subscription-couponcode-status subscription-couponcode-status--${normalizedStatus}`;
  };

  return (
    <div className="subscription-couponcode-list-container">
      <h2 className="admin-section-title-row subscription-couponcode-heading">
        <FiList aria-hidden="true" />
        <span>Manage Subscription Coupons</span>
      </h2>
      {loading ? (
        <div className="subscription-couponcode-empty-state" role="status">
          <FiList aria-hidden="true" />
          <h3>Loading coupons</h3>
        </div>
      ) : (
        <div className="subscription-couponcode-list-body">
          {editingCoupon ? (
            <div className="subscription-couponcode-edit-container">
              <h3 className="admin-section-title-row subscription-couponcode-subheading">
                <FiEdit2 aria-hidden="true" />
                <span>Edit Coupon</span>
              </h3>
              <form className="subscription-couponcode-form">
                <label className="subscription-couponcode-edit-field">
                  <span className="subscription-couponcode-label">Name</span>
                  <input
                    className="subscription-couponcode-input"
                    type="text"
                    value={editingCoupon.name}
                    onChange={(e) => setEditingCoupon({ ...editingCoupon, name: e.target.value })}
                  />
                </label>
                <label className="subscription-couponcode-edit-field">
                  <span className="subscription-couponcode-label">Discount Amount</span>
                  <input
                    className="subscription-couponcode-input"
                    type="number"
                    value={editingCoupon.discountAmount}
                    onChange={(e) =>
                      setEditingCoupon({ ...editingCoupon, discountAmount: e.target.value })
                    }
                  />
                </label>
                <label className="subscription-couponcode-edit-field">
                  <span className="subscription-couponcode-label">Discount Type</span>
                  <select
                    className="subscription-couponcode-input"
                    value={editingCoupon.discountType}
                    onChange={(e) =>
                      setEditingCoupon({ ...editingCoupon, discountType: e.target.value })
                    }
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed</option>
                  </select>
                </label>
                <label className="subscription-couponcode-edit-field">
                  <span className="subscription-couponcode-label">Valid For</span>
                  <select
                    className="subscription-couponcode-input"
                    value={editingCoupon.validFor}
                    onChange={(e) => setEditingCoupon({ ...editingCoupon, validFor: e.target.value })}
                  >
                    <option value="monthly">Monthly</option>
                    <option value="annually">Annually</option>
                    <option value="both">Both</option>
                  </select>
                </label>
                <div className="subscription-couponcode-button-group">
                  <button type="button" className="subscription-couponcode-save-btn" onClick={handleSaveEdit}>
                    <FiSave aria-hidden="true" />
                    Save Changes
                  </button>
                  <button type="button" className="subscription-couponcode-cancel-btn" onClick={() => setEditingCoupon(null)}>
                    <FiX aria-hidden="true" />
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          ) : (
            <>
              {coupons.length === 0 ? (
                <div className="subscription-couponcode-empty-state" role="status">
                  <FiList aria-hidden="true" />
                  <h3>No coupons yet</h3>
                  <p>New subscription coupon codes will appear here.</p>
                </div>
              ) : (
                <div className="subscription-couponcode-table-shell">
                  <table className="subscription-couponcode-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Discount</th>
                        <th>Valid For</th>
                        <th>Max Redemptions</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {coupons.map((coupon) => (
                        <tr key={coupon._id}>
                          <td>{coupon.name}</td>
                          <td>{formatDiscount(coupon)}</td>
                          <td>{coupon.validFor}</td>
                          <td>{coupon.maxRedemptions}</td>
                          <td>
                            <span className={getStatusClassName(coupon.status)}>{coupon.status || 'Active'}</span>
                          </td>
                          <td>
                            <button
                              className="subscription-couponcode-action-btn"
                              onClick={() => handleEditCoupon(coupon)}
                              aria-label={`Edit ${coupon.name}`}
                            >
                              <FiEdit2 aria-hidden="true" />
                              Edit
                            </button>
                            <button
                              className="subscription-couponcode-action-btn delete"
                              onClick={() => handleDeleteCoupon(coupon._id)}
                              aria-label={`Delete ${coupon.name}`}
                            >
                              <FiTrash2 aria-hidden="true" />
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ManageSubscriptionCoupons;


